import type { ServiceSection } from "@/components/ServiceSections";

export interface PurificationServiceContent {
  title: string;
  metaDesc: string;
  heroDesc: string;
  benefits: string[];
  faqs: { question: string; answer: string }[];
  sections: ServiceSection[];
}

export const purificationContent: Record<string, PurificationServiceContent> = {
  "water-softeners": {
    title: "Water Softener Installation — Phoenix, Tolleson & the West Valley",
    metaDesc:
      "Water softener installation in Phoenix, Tolleson and the West Valley. Combat 15–25 gpg hard water. Salt-based & salt-free options. Free water test. Call (480) 322-2567.",
    heroDesc:
      "Phoenix and the West Valley have some of the hardest water in the nation — 15 to 25 grains per gallon, compared to a national average of 7. That mineral-loaded water is destroying your pipes, shortening appliance lifespans, and leaving your skin dry and hair brittle. A properly sized water softener changes everything — and we install them across Phoenix, Tolleson, Glendale, Peoria, Surprise, Goodyear and the entire West Valley.",
    benefits: [
      "Extends water heater lifespan by 30–50%",
      "Eliminates scale buildup in pipes and fixtures",
      "Softer skin, smoother hair, cleaner laundry",
      "Reduces soap and detergent use by up to 50%",
      "Protects dishwashers, washing machines, and ice makers",
    ],
    faqs: [
      {
        question: "Do I need a water softener in Phoenix?",
        answer:
          "Yes — for most Phoenix and West Valley homeowners, a water softener pays for itself. Phoenix-area municipal water typically tests at 15–20 grains per gallon (gpg), which is 3–4 times the national average. Without softening, hard water minerals shorten water heater life by 30–50%, scale up pipes and fixtures, and dramatically reduce dishwasher and washing machine efficiency. The exception: if you rent and don't pay for appliance replacement, the case is weaker.",
      },
      {
        question: "How hard is Phoenix and Tolleson water?",
        answer:
          "Phoenix metro municipal water — including Tolleson, Glendale, Peoria, Surprise, and Avondale — typically tests at 15–20 gpg. Anything above 7 gpg is considered hard, and above 10.5 gpg is 'very hard.' For reference, much of the East Coast runs 3–5 gpg. West Valley water is 3–4 times harder than the national average, which is why softeners are nearly standard equipment here.",
      },
      {
        question: "Do no-salt water softeners work in Phoenix?",
        answer:
          "Salt-free conditioners can reduce scale buildup, but they do not actually remove hard minerals — they alter the crystal structure so minerals are less likely to stick. For Phoenix's extreme hardness (15+ gpg), salt-free systems give partial results at best. Salt-based ion-exchange softeners remain the gold standard for Phoenix-area water if you want truly soft water, longer appliance life, and complete scale elimination.",
      },
      {
        question: "Salt-based or salt-free — which should I choose?",
        answer:
          "Salt-based softeners actually remove hard minerals (ion exchange) and are the gold standard for Arizona's extreme hardness. Salt-free conditioners don't remove minerals — they alter their structure to reduce (not eliminate) scaling. For West Valley water, we typically recommend salt-based systems.",
      },
      {
        question: "How much salt does a water softener use?",
        answer:
          "Modern high-efficiency softeners use 40–80 pounds of salt per month for a typical Arizona family of four. That's about $5–10/month in salt. The savings on reduced soap use, longer appliance life, and fewer plumbing repairs far outweigh the cost.",
      },
      {
        question: "How much does a water softener cost in Phoenix?",
        answer:
          "Installed water softener systems in the Phoenix area typically range from $1,200 to $3,500 depending on capacity, efficiency, and brand. High-efficiency systems sized for Phoenix's extreme hardness cost more upfront but use less salt and water during regeneration. We provide upfront written quotes after a free in-home water test — no high-pressure sales.",
      },
    ],
    sections: [
      {
        type: "stats",
        items: [
          { value: "15–25", label: "Grains per gallon in Phoenix & West Valley water" },
          { value: "7 gpg", label: "National average water hardness" },
          { value: "50%", label: "Reduction in soap and detergent use" },
          { value: "30–50%", label: "Longer water heater lifespan" },
        ],
      },
      {
        type: "prose",
        heading: "Why Phoenix Homes Need a Water Softener More Than Most",
        paragraphs: [
          "Phoenix sits in one of the hardest-water regions in the United States. Water from the Colorado River, Salt River Project canals, and local groundwater aquifers all flow through mineral-rich desert geology before reaching your home. By the time it comes out of your tap in Phoenix, Tolleson, Glendale or Peoria, it carries 15–25 grains per gallon of calcium and magnesium — three to four times the national average.",
          "That hardness has measurable consequences. Phoenix water heaters fail years earlier than the same models installed in soft-water regions. Dishwashers and washing machines need repair more often. Faucets and showerheads scale up within months of installation. Glass shower doors etch permanently. And the new home you bought five years ago? Its plumbing is already accumulating scale inside copper and PEX lines you'll never see until pressure starts dropping.",
          "A properly sized water softener installed at the main supply line addresses all of it at the source. For a Phoenix-area home of 2,000–3,500 square feet with a family of four, a 32,000–48,000 grain system handles the load with reasonable salt use and regeneration frequency. We size every install based on your actual water test, household size, and water usage — not a one-size-fits-all recommendation.",
        ],
      },
      {
        type: "comparison",
        heading: "Salt-Based vs. Salt-Free Water Treatment",
        optionA: {
          title: "Salt-Based Softener",
          points: [
            "Actually removes calcium and magnesium",
            "Eliminates scale completely",
            "Best for Arizona's extreme hardness (15+ gpg)",
            "Requires salt refills every 4–6 weeks",
            "Produces soft-feeling water",
          ],
        },
        optionB: {
          title: "Salt-Free Conditioner",
          points: [
            "Doesn't remove minerals — alters their structure",
            "Reduces scaling but doesn't eliminate it",
            "Better suited for moderate hardness (under 10 gpg)",
            "No salt or maintenance chemicals needed",
            "Water doesn't feel 'soft' — minerals remain",
          ],
        },
      },
      {
        type: "prose",
        heading: "What Arizona Hard Water Actually Does to Your Home",
        paragraphs: [
          "Hard water damage is slow and invisible until it isn't. Inside your water heater, minerals settle to the bottom with every heating cycle, forming an insulating layer that forces the unit to work harder. After 3–5 years in Arizona without a softener, that sediment layer can be inches thick — reducing efficiency by 25% and cutting the unit's lifespan nearly in half.",
          "Your pipes tell a similar story. Copper water lines in West Valley homes develop mineral scale on their interior walls, gradually reducing water flow. After 10–15 years, that 3/4-inch pipe may have an effective diameter closer to 1/2 inch. You notice it as reduced water pressure, but the real problem is inside the pipe.",
          "Then there's everything you can see: white crust on faucets, spotty glasses, stiff laundry, dry skin after showering. A water softener addresses all of it at the source, protecting your home's plumbing infrastructure while making daily life noticeably more comfortable.",
        ],
      },
    ],
  },

  "whole-home-filtration": {
    title: "Whole Home Water Filtration in Tolleson, AZ",
    metaDesc:
      "Whole home water filtration in Tolleson, AZ. Remove chlorine, sediment, and contaminants from every tap. Professional installation. Call (480) 322-2567.",
    heroDesc:
      "Filtered water from every faucet in your home — not just the kitchen. Whole-home filtration removes chlorine, sediment, and chemical byproducts before they reach your showers, sinks, and appliances. In Tolleson and the West Valley, where municipal water travels long distances through aging infrastructure, filtration makes a real difference.",
    benefits: [
      "Clean, filtered water from every tap and shower",
      "Removes chlorine taste and odor",
      "Reduces sediment and particulate matter",
      "Protects fixtures and appliances from debris",
      "Single system covers the entire house",
    ],
    faqs: [
      {
        question: "What does whole home filtration remove?",
        answer:
          "Depending on the system, it removes chlorine, chloramines, sediment, rust particles, some VOCs, and improves taste and odor. For specific contaminants like lead, arsenic, or bacteria, additional point-of-use filtration (like reverse osmosis) may be needed.",
      },
      {
        question: "How often do filters need replacing?",
        answer:
          "Pre-filters (sediment) typically need replacement every 3–6 months. Carbon filters last 6–12 months. We offer maintenance plans that include filter changes on schedule so you never forget.",
      },
      {
        question: "Does this replace a water softener?",
        answer:
          "No. Filtration and softening do different jobs. Filtration removes particles and chemicals. Softening removes hardness minerals (calcium/magnesium). Many Arizona homes benefit from both, installed in sequence: softener first, then filtration.",
      },
    ],
    sections: [
      {
        type: "process",
        heading: "Our Whole Home Filtration Process",
        steps: [
          {
            title: "Water Quality Test",
            desc: "We test your water for chlorine, TDS, pH, sediment, and hardness to recommend the right filtration system for your specific water profile.",
          },
          {
            title: "Custom Installation",
            desc: "The system installs at your main water line entry point, filtering all water before it reaches any fixture. We handle plumbing, mounting, and bypass valve setup.",
          },
          {
            title: "Ongoing Maintenance",
            desc: "Filter changes on schedule, system checks, and water quality re-testing to ensure your system continues performing. We offer annual maintenance plans.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Why West Valley Water Needs Filtration",
        paragraphs: [
          "Tolleson and surrounding cities receive water from a mix of Salt River Project surface water and groundwater wells. That water is treated at municipal facilities and chlorinated for safety — which is necessary, but it means your tap water carries chlorine and its byproducts by the time it reaches your home.",
          "The bigger issue in many West Valley neighborhoods is infrastructure age. Water mains in parts of Tolleson, Glendale, and West Phoenix are decades old. As water travels through aging pipes to your meter, it can pick up rust particles, sediment, and pipe material. You might see this as slightly discolored water after a main break or hydrant flush, but low-level sediment enters your home daily.",
          "A whole-home filtration system catches all of this before it reaches your water heater (where sediment accelerates buildup), your fixtures (where particles scratch surfaces), and your body (where chlorine dries skin and hair). It's the foundation layer of a complete water treatment approach.",
        ],
      },
      {
        type: "stats",
        items: [
          { value: "99%", label: "Chlorine removal with carbon filtration" },
          { value: "5 μm", label: "Sediment particle capture size" },
          { value: "1 system", label: "Covers every faucet in your home" },
          { value: "6–12 mo", label: "Filter change interval" },
        ],
      },
    ],
  },

  "reverse-osmosis": {
    title: "Reverse Osmosis Systems — Pure Drinking Water in Tolleson",
    metaDesc:
      "Reverse osmosis installation in Tolleson, AZ. Remove up to 99% of contaminants. Better than bottled water for pennies per gallon. Call (480) 322-2567.",
    heroDesc:
      "Arizona tap water contains 500–800 parts per million of total dissolved solids. Bottled water? Typically under 50 ppm. A reverse osmosis system installed under your kitchen sink delivers bottled-water purity at a fraction of the cost — pennies per gallon instead of dollars.",
    benefits: [
      "Removes up to 99% of dissolved solids, lead, and arsenic",
      "Better-than-bottled-water quality from your tap",
      "Costs 3–5 cents per gallon vs. $1+ for bottles",
      "Under-sink installation — no counter space needed",
      "Optional connection to refrigerator and ice maker",
    ],
    faqs: [
      {
        question: "What does reverse osmosis remove?",
        answer:
          "RO systems remove lead, arsenic, fluoride, chlorine, nitrates, sodium, dissolved solids, pharmaceuticals, and many other contaminants. They produce some of the purest drinking water available — typically 10–30 ppm TDS from Arizona source water of 500–800 ppm.",
      },
      {
        question: "How much does an RO system cost to operate?",
        answer:
          "Filter and membrane replacement costs about $60–100 per year. At 3–5 cents per gallon of purified water, a family of four saves $500–1,000 annually compared to buying bottled water.",
      },
      {
        question: "Does RO waste water?",
        answer:
          "Traditional RO systems use 3–4 gallons of water to produce 1 gallon of purified water. Modern high-efficiency systems have a 1:1 or 2:1 ratio. We install high-efficiency models to minimize waste, especially important in water-conscious Arizona.",
      },
    ],
    sections: [
      {
        type: "stats",
        items: [
          { value: "99%", label: "TDS removal rate" },
          { value: "3–5¢", label: "Cost per gallon of purified water" },
          { value: "500–800", label: "PPM in Arizona tap water" },
          { value: "10–30", label: "PPM after RO treatment" },
        ],
      },
      {
        type: "comparison",
        heading: "RO vs. Bottled Water vs. Pitcher Filters",
        optionA: {
          title: "Reverse Osmosis",
          points: [
            "99% contaminant removal",
            "3–5 cents per gallon",
            "Unlimited supply from your tap",
            "No plastic waste",
            "Consistent quality — not brand-dependent",
          ],
        },
        optionB: {
          title: "Bottled / Pitcher Filters",
          points: [
            "Bottled: $1–3 per gallon, variable quality",
            "Pitchers: limited to chlorine and taste",
            "Neither removes dissolved solids effectively",
            "Ongoing plastic waste (bottled)",
            "Inconvenient refills and purchases",
          ],
        },
      },
      {
        type: "prose",
        heading: "Why Arizona Tap Water TDS Makes RO Worth It",
        paragraphs: [
          "Total Dissolved Solids (TDS) is the measure of everything dissolved in your water — minerals, salts, metals, and organic compounds. The EPA sets a secondary guideline of 500 ppm for taste. West Valley tap water routinely tests at 500–800 ppm, right at or above that threshold.",
          "You taste it. That slightly mineral, slightly off flavor of Arizona tap water is TDS. It's why many families default to buying bottled water cases from Costco — spending $30–60 a month for what a $300–600 RO system produces for pennies.",
          "An under-sink RO system connects to a dedicated faucet and optionally to your refrigerator's water and ice lines. The result is clear, clean, neutral-tasting water on demand. No more bottle runs, no more plastic waste, no more wondering if the bottled water is actually better (some brands are just filtered municipal water anyway).",
        ],
      },
    ],
  },

  "arizona-hard-water": {
    title: "Arizona Hard Water Solutions — Protect Your Tolleson Home",
    metaDesc:
      "Arizona hard water solutions in Tolleson, AZ. Expert water testing, softener installation, and whole-home treatment. Local team, free assessment. Call (480) 322-2567.",
    heroDesc:
      "Arizona's water is among the hardest in the United States — and it's not a minor inconvenience. Hard water shortens the life of every water-using appliance in your home, damages your plumbing from the inside, and costs you money in wasted energy and premature replacements. We provide customized solutions based on your actual water quality, not a one-size-fits-all approach.",
    benefits: [
      "Free comprehensive water quality testing",
      "Solutions tailored to your home's specific water profile",
      "Combination systems (softener + filtration + RO)",
      "Ongoing support and maintenance plans",
      "Local experts who live with the same water you do",
    ],
    faqs: [
      {
        question: "Why is Arizona water so hard?",
        answer:
          "Arizona's water comes from the Colorado River and underground aquifers that flow through mineral-rich desert rock. The hot, arid climate means less dilution from rainfall. As water travels through limestone and geological formations, it dissolves calcium, magnesium, and other minerals at high concentrations.",
      },
      {
        question: "What's the best solution for my home?",
        answer:
          "It depends on your water test results and your priorities. Most West Valley homes benefit from a water softener (for pipes and appliances) combined with a reverse osmosis system (for drinking water). Some homes add whole-home filtration for chlorine removal. We test first, then recommend.",
      },
      {
        question: "How much does hard water damage actually cost?",
        answer:
          "Studies show hard water reduces water heater efficiency by up to 48% and shortens appliance lifespans by 30–50%. For a typical Arizona home, that's $1,500–3,000 in premature appliance replacement costs over 10 years, plus higher monthly energy bills.",
      },
    ],
    sections: [
      {
        type: "prose",
        heading: "The Colorado River and Your Pipes",
        paragraphs: [
          "Much of the West Valley's water originates from the Colorado River, delivered via the Central Arizona Project canal. The Colorado flows through the Grand Canyon and hundreds of miles of limestone before reaching Arizona — picking up dissolved minerals the entire way. By the time it's treated and delivered to Tolleson, it carries 15–25 grains per gallon of hardness minerals.",
          "Local groundwater wells add another variable. Depending on the aquifer depth and geology, well water in the West Valley can test even harder than surface water. Some Buckeye and far-west areas see levels above 30 gpg — extreme by any standard.",
          "This isn't a problem you can ignore or adapt to. Hard water is actively degrading your home's plumbing system every day. The question isn't whether to treat it — it's what combination of treatment systems makes sense for your home's specific water profile and your family's needs.",
        ],
      },
      {
        type: "warnings",
        heading: "Signs Hard Water Is Damaging Your Home",
        items: [
          "White or yellowish mineral crust on faucets and showerheads",
          "Dry, itchy skin and brittle hair after showering",
          "Spots and film on glasses even after dishwasher cycle",
          "Laundry feels stiff, colors fade faster than expected",
          "Water heater rumbling or popping (sediment buildup)",
          "Reduced water flow from fixtures (scale-narrowed pipes)",
        ],
      },
      {
        type: "stats",
        items: [
          { value: "48%", label: "Efficiency loss in water heaters from hard water" },
          { value: "30–50%", label: "Shorter appliance lifespan" },
          { value: "15–25", label: "GPG hardness in West Valley (vs. 7 national avg)" },
          { value: "$1,500+", label: "Extra costs over 10 years from hard water damage" },
        ],
      },
    ],
  },
};
